import React, { useState } from 'react';
import { Group } from 'react-konva';

import { useLatest } from 'src/hooks/useLatest';
import CylinderBorder from './cylinderBorder';
import MoveGrabbers from './moveGrabbers';
import RadiusGrabbers from './radiusGrabbers';
import ReferenceGrabbers from './referenceGrabbers';
import Rotation from './rotation';
import type { CylinderType } from 'src/types/transforms';

type Point = {
    x: number;
    y: number;
};

type CylinderWarpProps = {
    container: HTMLElement;
    documentId: string;
    cylinderWarp?: CylinderType;
    scale: number;
    hidden?: boolean;
    selected: boolean;
    cursorChange: (cursor: string) => void;
    selectLayer: (documentId: string) => void;
    offset: Point;
    updateCylinder: (params: { id: string; cylinder: any }) => void;
};

const CylinderWarp: React.FC<CylinderWarpProps> = (props) => {
    const { scale, offset, cursorChange, container, selected, documentId, selectLayer, hidden, updateCylinder } = props;

    // `dragging`/`cylinderWarp` are kept as one state object, updated via a single `setState` call
    // per transition, rather than two independent `useState` hooks -- this tree is mounted inside
    // react-konva's <Stage>, under React's legacy root mode, so updates triggered by Konva's own
    // event dispatch (as `onDragMove` below is) don't get React 18's automatic batching. Two
    // separate setters here means `setCylinderWarp(newCylinderWarp)` commits its own render first,
    // with `dragging` still `false`, which triggers the "sync back to props while not dragging"
    // check below and reverts `cylinderWarp` to `props.cylinderWarp` before `setDragging(true)`
    // commits -- the net effect is the cylinder doesn't visibly move on the very first drag-move
    // event of every gesture. Combining into one atomic update avoids the intermediate
    // `!dragging`-but-already-moved render entirely. (See rulers.tsx for the sibling bug where the
    // same split caused an outright crash instead of a dropped frame.)
    const [dragState, setDragState] = useState<{ dragging: boolean; cylinderWarp?: CylinderType }>({
        dragging: false,
        cylinderWarp: props.cylinderWarp,
    });
    const { dragging, cylinderWarp } = dragState;

    if (!dragging && cylinderWarp !== props.cylinderWarp) {
        setDragState({ dragging: false, cylinderWarp: props.cylinderWarp });
    }

    // CylinderBorder/Rotation/MoveGrabbers/RadiusGrabbers/ReferenceGrabbers' `useDragGesture` calls
    // mirror the latest `onDragEnd` they're given every render, but that closure's own body still
    // needs a *live* `cylinderWarp` at call time -- `useLatest` covers that (see its doc comment),
    // the same way the sibling `nPointWarp.tsx` does with `pointsRef`.
    const cylinderWarpRef = useLatest(cylinderWarp);

    const onDragMove = (newCylinderWarp: any) => {
        cylinderWarpRef.current = newCylinderWarp;
        setDragState({ dragging: true, cylinderWarp: newCylinderWarp });
    };

    const onDragEnd = () => {
        updateCylinder({ id: documentId, cylinder: cylinderWarpRef.current });
        setDragState((prev) => ({ ...prev, dragging: false }));
    };

    if (!cylinderWarp || !cylinderWarp.cylinderTop || hidden) {
        return null;
    }

    const scaledCylinderWarp: Omit<CylinderType, 'id' | 'fullWrap'> = {
        cylinderTop: {
            center: {
                x: cylinderWarp.cylinderTop.center.x * scale + offset.x,
                y: cylinderWarp.cylinderTop.center.y * scale + offset.y,
            },
            radiusX: cylinderWarp.cylinderTop.radiusX * scale,
            radiusY: cylinderWarp.cylinderTop.radiusY * scale,
            rotationDegrees: cylinderWarp.cylinderTop.rotationDegrees,
        },
        cylinderBottom: {
            center: {
                x: cylinderWarp.cylinderBottom.center.x * scale + offset.x,
                y: cylinderWarp.cylinderBottom.center.y * scale + offset.y,
            },
            radiusX: cylinderWarp.cylinderBottom.radiusX * scale,
            radiusY: cylinderWarp.cylinderBottom.radiusY * scale,
            rotationDegrees: cylinderWarp.cylinderBottom.rotationDegrees,
        },
        decorationAreaDimensions: {
            width: cylinderWarp.decorationAreaDimensions.width * scale,
            height: cylinderWarp.decorationAreaDimensions.height * scale,
        },
        productDiameter: cylinderWarp.productDiameter * scale,
        decorationExtentDegreesAlpha: cylinderWarp.decorationExtentDegreesAlpha,
        decorationExtentDegreesBeta: cylinderWarp.decorationExtentDegreesBeta,
        isDecorationRotated: cylinderWarp.isDecorationRotated,
        imageDimensions: {
            width: cylinderWarp.imageDimensions.width * scale,
            height: cylinderWarp.imageDimensions.height * scale,
        },
    };

    return (
        <Group>
            <CylinderBorder
                cylinderWarp={cylinderWarp}
                scaledCylinderWarp={scaledCylinderWarp}
                scale={scale}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
                selected={selected}
                selectLayer={() => selectLayer(documentId)}
            />
            {selected && (
                <Group>
                    <Rotation
                        cylinderWarp={cylinderWarp}
                        scaledCylinderWarp={scaledCylinderWarp}
                        scale={scale}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        cursorChange={cursorChange}
                        container={container}
                        offset={offset}
                    />
                    <MoveGrabbers
                        cylinderWarp={cylinderWarp}
                        scaledCylinderWarp={scaledCylinderWarp}
                        scale={scale}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        cursorChange={cursorChange}
                    />
                    <RadiusGrabbers
                        cylinderWarp={cylinderWarp}
                        scaledCylinderWarp={scaledCylinderWarp}
                        scale={scale}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        cursorChange={cursorChange}
                        container={container}
                        offset={offset}
                    />
                    <ReferenceGrabbers
                        cylinderWarp={cylinderWarp}
                        scaledCylinderWarp={scaledCylinderWarp}
                        scale={scale}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        cursorChange={cursorChange}
                        container={container}
                        offset={offset}
                    />
                </Group>
            )}
        </Group>
    );
};

export default CylinderWarp;
